import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrganizationSettings } from "@/hooks/useOrganizationSettings";
import { toast } from "sonner";
import { CreditCard, Save } from "lucide-react";

const PaymentSettings = () => {
  const { getSetting, updateSetting, loading } = useOrganizationSettings();
  const [swiftAccountId, setSwiftAccountId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Update local state when settings are loaded
  useEffect(() => {
    const savedValue = getSetting('send_swift_account_id') || '';
    setSwiftAccountId(savedValue);
  }, [loading]); // Only depend on loading, not getSetting

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSetting(
        'send_swift_account_id',
        swiftAccountId,
        'Third-party service ID for Swift payments - skips payment step when set'
      );
      toast.success('Payment settings updated successfully');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error('Failed to save payment settings');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = swiftAccountId !== (getSetting('send_swift_account_id') || '');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          <CardTitle>Payment Settings</CardTitle>
        </div>
        <CardDescription>
          Configure payment processing options for your organization
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Swift Account ID */}
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="swift-account-id">Send Swift Account ID</Label>
            <p className="text-sm text-muted-foreground">
              Third-party service ID for direct Swift payments. When set, quote acceptance 
              automatically books the shipment without requiring payment links.
            </p>
          </div>
          <Input
            id="swift-account-id"
            placeholder="Enter Swift Account ID"
            value={swiftAccountId}
            onChange={(e) => setSwiftAccountId(e.target.value)}
            disabled={loading || isSaving}
          />
        </div>

        {/* Future: Payment Gateway Settings */}
        <div className="space-y-3 opacity-50">
          <div className="space-y-1">
            <Label>Payment Gateway (Coming Soon)</Label>
            <p className="text-sm text-muted-foreground">
              Configure Payfast, Stitch, or other payment gateways for automated payment processing.
            </p>
          </div>
          <Input
            placeholder="Payment gateway configuration"
            disabled
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || loading}
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Payment Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentSettings;
